import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AuthContext } from '../App';

const CATEGORIES = ['hangout', 'gaming', 'esports', 'anime', 'music', 'art', 'craft', 'coding', 'education', 'focus', 'fitness'];
const PRESET_TAGS = ['chill', 'competitive', 'educational', 'first playthrough', 'speedrun', 'just chatting', 'variety', 'beginner friendly', 'no commentary', 'highlights'];

// Dashboard pixel art icons
const DPX = 2;
const DC = { a: '#D6E9AA', A: '#e4f2c1', w: '#efeff1', W: '#ffffff', d: '#2a2a2e', s: '#adadb8', g: '#00c400', r: '#eb0400', y: '#ffd700', Y: '#ffe866', k: '#ff6b9d', b: '#64b5f6', B: '#90caf9' };
function DashIcon({ rows }) {
  const shadows = [];
  const parsed = rows.map(r => r.split(''));
  for (let y = 0; y < parsed.length; y++)
    for (let x = 0; x < parsed[y].length; x++) {
      const ch = parsed[y][x];
      if (ch !== '.' && DC[ch]) shadows.push(`${x*DPX}px ${y*DPX}px 0 ${DC[ch]}`);
    }
  const w = Math.max(...parsed.map(r => r.length));
  return <div style={{ width: w*DPX, height: parsed.length*DPX, position: 'relative', imageRendering: 'pixelated', flexShrink: 0 }}>
    <div style={{ position: 'absolute', width: DPX, height: DPX, boxShadow: shadows.join(',') }} />
  </div>;
}

export default function DashboardPage() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('gaming');
  const [tags, setTags] = useState([]);
  const [learningTags, setLearningTags] = useState([]);
  const [learningInput, setLearningInput] = useState('');
  const [goalText, setGoalText] = useState('');
  const [goalTarget, setGoalTarget] = useState(100);
  const [liveMsg, setLiveMsg] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [copied, setCopied] = useState('');
  const [liveChannels, setLiveChannels] = useState([]);
  const [showRaid, setShowRaid] = useState(false);
  const [raiding, setRaiding] = useState(false);

  // Moderation
  const [bans, setBans] = useState([]);
  const [mods, setMods] = useState([]);
  const [modInput, setModInput] = useState('');
  const [modMsg, setModMsg] = useState('');

  // Channel points
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [rewardTitle, setRewardTitle] = useState('');
  const [rewardCost, setRewardCost] = useState(500);
  const [rewardDesc, setRewardDesc] = useState('');
  const [rewardMsg, setRewardMsg] = useState('');

  // Chat settings
  const [chatSettings, setChatSettings] = useState({
    slow_mode_seconds: 0, follower_only: false, sub_only: false,
    links_allowed: true, account_age_required: false,
  });
  const [chatSettingsMsg, setChatSettingsMsg] = useState('');

  // Polls
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollDuration, setPollDuration] = useState(60);
  const [pollMsg, setPollMsg] = useState('');
  const [activePoll, setActivePoll] = useState(null);

  // Channel Panels
  const [panels, setPanels] = useState([]);
  const [panelTitle, setPanelTitle] = useState('');
  const [panelContent, setPanelContent] = useState('');
  const [panelMsg, setPanelMsg] = useState('');

  useEffect(() => {
    if (!user?.is_streamer) return;
    const loadDashboard = (retries = 3) => {
      api.dashboard()
        .then(d => { setData(d); setLoading(false); })
        .catch(() => {
          if (retries > 0) setTimeout(() => loadDashboard(retries - 1), 2000);
          else setLoading(false);
        });
    };
    loadDashboard();
    api.listRewards(user.username).then(setRewards).catch(() => {});
    api.redemptions().then(setRedemptions).catch(() => {});
    api.listBans(user.username).then(setBans).catch(() => {});
    api.listMods(user.username).then(setMods).catch(() => {});
    api.chatSettings(user.username).then(setChatSettings).catch(() => {});
    api.activePoll(user.username).then(p => { if (p) setActivePoll(p); }).catch(() => {});
    api.panels(user.username).then(setPanels).catch(() => {});
    api.payoutHistory().then(setPayoutData).catch(() => {});
  }, [user]);

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleGoLive = async () => {
    if (!title.trim()) { setLiveMsg('Please enter a stream title'); return; }
    setLiveMsg('');
    try {
      await api.goLive({ title, category, tags, learning_tags: learningTags, goal_text: goalText, goal_target: goalTarget });
      setIsLive(true);
      setLiveMsg(`🔴 You're live! Connect OBS to the RTMP URL with your stream key.`);
    } catch (e) {
      setLiveMsg(`❌ ${e.message}`);
    }
  };

  const handleEndStream = async () => {
    try {
      await api.endStream();
      setIsLive(false);
      setLiveMsg('Stream ended.');
      setShowRaid(false);
    } catch (e) {}
  };

  const handleOpenRaid = async () => {
    const channels = await api.liveChannels().catch(() => []);
    setLiveChannels(channels.filter(c => c.username !== user?.username));
    setShowRaid(true);
  };

  const handleRaid = async (targetUsername) => {
    setRaiding(true);
    try {
      await api.raid(targetUsername);
      setLiveMsg(`🎉 Raiding ${targetUsername}!`);
      setShowRaid(false);
      setTimeout(() => { navigate(`/${targetUsername}`); }, 2000);
    } catch (e) {
      setLiveMsg(`❌ ${e.message}`);
    } finally {
      setRaiding(false);
    }
  };

  // Dashboard tabs
  const [activeSection, setActiveSection] = useState('overview');

  // Chart
  const [chartStat, setChartStat] = useState('revenue');
  const [chartPeriod, setChartPeriod] = useState('monthly');
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  // Payouts
  const [payoutEmail, setPayoutEmail] = useState(user?.payout_email || '');
  const [payoutData, setPayoutData] = useState(null);
  const [payoutMsg, setPayoutMsg] = useState('');

  if (!user?.is_streamer) {
    return (
      <div className="dashboard">
        <h1>Dashboard</h1>
        <div className="offline-banner">
          <p>This dashboard is for streamers only.</p>
          <p style={{ marginTop: '8px', color: '#adadb8' }}>Sign up as a streamer to access this page.</p>
        </div>
      </div>
    );
  }

  // Don't block the whole page while loading — show tabs with loading indicator

  const earnings = data ? (data.tips_earnings_cents + data.subscriptions_monthly_cents) / 100 : 0;

  const DASH_ICONS = {
    overview: [ // bar chart
      '........',
      '..a.....',
      '..a.a...',
      '..a.a.a.',
      '..a.a.a.',
      '..a.a.a.',
      '..a.a.a.',
      'ssssssss',
    ],
    stream: [ // camera/play
      '.wwwwww.',
      'wddddddw',
      'wd.ggddw',
      'wd.gggdw',
      'wd.ggddw',
      'wddddddw',
      '.wwwwww.',
      '..w..w..',
    ],
    earnings: [ // coin
      '..yyyy..',
      '.yYYYYy.',
      'yYyddYYy',
      'yYdddYYy',
      'yYyddYYy',
      '.yYYYYy.',
      '..yyyy..',
      '........',
    ],
    panels: [ // clipboard
      '.ssssss.',
      '.swwwws.',
      '.s.ss.s.',
      '.ssssss.',
      '.s.ss.s.',
      '.ssssss.',
      '.s.ss.s.',
      '.ssssss.',
    ],
    chat: [ // speech bubble
      '.kkkkkk.',
      'kkkkkkkk',
      'kkddkkdk',
      'kkkkkkkk',
      'kkdddkkk',
      'kkkkkkkk',
      '..kk....',
      '.kk.....',
    ],
    moderation: [ // shield
      '.bbbbbb.',
      'bBBBBBBb',
      'bBBwBBBb',
      'bBwwwBBb',
      'bBBwBBBb',
      '.bBBBBb.',
      '..bBBb..',
      '...bb...',
    ],
    points: [ // diamond/gem
      '...aa...',
      '..aAAa..',
      '.aAAAAa.',
      'aAAAAAAa',
      '.aAAAAa.',
      '..aAAa..',
      '...aa...',
      '........',
    ],
  };

  const SECTIONS = [
    { id: 'overview', label: 'Overview' },
    { id: 'stream', label: 'Stream Setup' },
    { id: 'earnings', label: 'Earnings' },
    { id: 'panels', label: 'Panels' },
    { id: 'chat', label: 'Chat Settings' },
    { id: 'moderation', label: 'Moderation' },
    { id: 'points', label: 'Channel Points' },
  ];

  return (
    <div className="dashboard" style={{ maxWidth: 1100 }}>
      <h1>Streamer Dashboard</h1>

      {/* Tab navigation */}
      <div className="dash-tabs">
        {SECTIONS.map(s => (
          <button key={s.id}
            className={`dash-tab ${activeSection === s.id ? 'active' : ''}`}
            onClick={() => setActiveSection(s.id)}>
            <span className="dash-tab-icon"><DashIcon rows={DASH_ICONS[s.id]} /></span>
            <span className="dash-tab-label">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Payout setup banner — earnings tab only */}
      {activeSection === 'earnings' && !user.payout_email && (
        <div className="onboard-banner">
          <h3>💰 Set up your PayPal to get paid</h3>
          <p>Add your PayPal email to receive 90% of tips and subscriptions.</p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <input className="input" placeholder="your@paypal-email.com" value={payoutEmail}
              onChange={e => setPayoutEmail(e.target.value)} style={{ marginBottom: 0, flex: 1, maxWidth: '300px' }} />
            <button className="btn btn-primary" onClick={async () => {
              try {
                const res = await api.updatePayout({ payout_email: payoutEmail });
                setPayoutMsg('PayPal email saved!');
                if (res.user) window.location.reload();
              } catch (e) { setPayoutMsg(e.message); }
              setTimeout(() => setPayoutMsg(''), 3000);
            }}>Save</button>
          </div>
          {payoutMsg && <p style={{ fontSize: '0.82rem', marginTop: '8px', color: payoutMsg.includes('saved') ? '#00c400' : '#eb0400' }}>{payoutMsg}</p>}
        </div>
      )}

      {/* Earnings & Payouts */}
      {activeSection === 'earnings' && user.payout_email && payoutData && (
        <div className="stream-setup">
          <h2>💰 Earnings & Payouts</h2>
          <div className="stats-grid" style={{ marginBottom: '16px' }}>
            <div className="stat-card">
              <div className="stat-label">Available</div>
              <div className="stat-value">${(payoutData.available / 100).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Earned</div>
              <div className="stat-value">${(payoutData.total_earned / 100).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Paid</div>
              <div className="stat-value">${(payoutData.total_paid / 100).toFixed(2)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.85rem', color: '#adadb8' }}>PayPal: <strong style={{ color: '#efeff1' }}>{user.payout_email}</strong></span>
            <button className="btn btn-secondary" style={{ fontSize: '0.78rem' }} onClick={() => {
              const newEmail = prompt('Update PayPal email:', user.payout_email);
              if (newEmail !== null) {
                api.updatePayout({ payout_email: newEmail }).then(() => window.location.reload()).catch(e => alert(e.message));
              }
            }}>Change</button>
          </div>
          <p style={{ fontSize: '0.8rem', color: '#adadb8', padding: '10px 14px', background: '#0e0e10', borderRadius: '4px' }}>
            Payouts are sent <strong style={{ color: '#efeff1' }}>weekly</strong> to your PayPal for balances over $10.00.
          </p>

          {payoutData.payouts.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase' }}>Payout History</div>
              {payoutData.payouts.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#0e0e10', borderRadius: '4px', marginBottom: '4px', fontSize: '0.82rem' }}>
                  <span>${(p.amount_cents / 100).toFixed(2)} → {p.payout_email}</span>
                  <span style={{ color: p.status === 'completed' ? '#00c400' : p.status === 'pending' ? '#ffa726' : '#adadb8', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem' }}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats — earnings tab only */}
      {activeSection === 'earnings' && data && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Your Earnings (tips)</div>
            <div className="stat-value">${(data.tips_earnings_cents / 100).toFixed(2)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Subscribers</div>
            <div className="stat-value">{data.subscribers}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Monthly (subs)</div>
            <div className="stat-value">${(data.subscriptions_monthly_cents / 100).toFixed(2)}</div>
          </div>
        </div>
      )}

      {/* Overview */}
      {activeSection === 'overview' && (
        <div>
          {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#adadb8' }}>Loading dashboard...</div>}

          {/* Live status banner — only when live */}
          {isLive && (
            <div className="stream-setup" style={{ marginBottom: '20px', borderLeft: '3px solid #00c400' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', color: '#00c400', animation: 'pixelBlink 1.5s step-start infinite' }}>LIVE</span>
                  <span style={{ fontWeight: 700 }}>{title || 'Untitled stream'}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary" onClick={handleOpenRaid}>Raid</button>
                  <button className="btn btn-danger" onClick={handleEndStream}>End Stream</button>
                </div>
              </div>
              {liveMsg && <p style={{ marginTop: '10px', fontSize: '0.85rem' }}>{liveMsg}</p>}
            </div>
          )}

          {/* Clickable stat cards */}
          <div className="stats-grid" style={{ marginBottom: '4px' }}>
            {[
              { key: 'followers', label: 'Followers', value: data?.follower_count || 0 },
              { key: 'subscribers', label: 'Subscribers', value: data?.subscribers || 0 },
              { key: 'streams', label: 'Total Streams', value: data?.total_streams || 0 },
              { key: 'hours', label: 'Hours Streamed', value: `${data?.total_hours || 0}h` },
              { key: 'views', label: 'Total Views', value: (data?.total_views || 0).toLocaleString() },
              { key: 'revenue', label: 'Revenue (90%)', value: `$${data ? ((data.tips_earnings_cents + data.subscriptions_monthly_cents) / 100).toFixed(2) : '0.00'}` },
            ].map(s => (
              <div key={s.key} className="stat-card" onClick={() => {
                setChartStat(s.key);
                setChartLoading(true);
                api.dashboardChart(s.key, chartPeriod).then(d => setChartData(d.data)).catch(() => setChartData([])).finally(() => setChartLoading(false));
              }} style={{ cursor: 'pointer', borderColor: chartStat === s.key ? 'var(--accent)' : '#2a2a2e', borderWidth: '1px', borderStyle: 'solid', transition: 'border-color 0.15s' }}>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="stream-setup" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <h2 style={{ margin: 0, textTransform: 'capitalize' }}>{chartStat === 'revenue' ? 'Revenue' : chartStat}</h2>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['weekly', 'monthly', 'yearly'].map(p => (
                  <button key={p} className={`btn ${chartPeriod === p ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '4px 12px', fontSize: '0.75rem', textTransform: 'capitalize' }}
                    onClick={() => {
                      setChartPeriod(p);
                      setChartLoading(true);
                      api.dashboardChart(chartStat, p).then(d => setChartData(d.data)).catch(() => setChartData([])).finally(() => setChartLoading(false));
                    }}>{p}</button>
                ))}
              </div>
            </div>

            {chartLoading ? (
              <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adadb8' }}>Loading...</div>
            ) : chartData.length === 0 ? (
              <div style={{ height: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#adadb8', fontSize: '0.85rem' }}>
                <p>No data yet for this period</p>
                <p style={{ fontSize: '0.75rem', marginTop: '4px' }}>Click a stat card above, then pick a time range</p>
              </div>
            ) : (
              <div style={{ position: 'relative', height: '180px' }}>
                {/* Y axis labels */}
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 20, width: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.65rem', color: '#adadb8' }}>
                    {chartStat === 'revenue' ? `$${(Math.max(...chartData.map(d => d.value), 1) / 100).toFixed(0)}` : Math.max(...chartData.map(d => d.value), 1)}
                  </span>
                  <span style={{ fontSize: '0.65rem', color: '#adadb8' }}>
                    {chartStat === 'revenue' ? `$${(Math.max(...chartData.map(d => d.value), 1) / 200).toFixed(0)}` : Math.round(Math.max(...chartData.map(d => d.value), 1) / 2)}
                  </span>
                  <span style={{ fontSize: '0.65rem', color: '#adadb8' }}>0</span>
                </div>
                {/* Chart area */}
                <div style={{ marginLeft: '55px', height: '160px', display: 'flex', alignItems: 'flex-end', gap: chartData.length > 15 ? '2px' : '6px', borderBottom: '1px solid #2a2a2e', paddingBottom: '1px' }}>
                  {chartData.map((d, i) => {
                    const maxVal = Math.max(...chartData.map(x => x.value), 1);
                    const pct = (d.value / maxVal) * 100;
                    const label = chartPeriod === 'yearly'
                      ? new Date(d.label + '-01').toLocaleDateString('en-US', { month: 'short' })
                      : new Date(d.label).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: 0 }} title={`${d.label}: ${chartStat === 'revenue' ? '$' + (d.value / 100).toFixed(2) : d.value}`}>
                        <div style={{
                          width: '100%', maxWidth: '36px',
                          background: 'var(--accent)',
                          borderRadius: '2px 2px 0 0',
                          height: `${Math.max(pct, 2)}%`,
                          transition: 'height 0.3s',
                          boxShadow: '1px 0 0 rgba(0,0,0,0.2)',
                          opacity: pct > 0 ? 1 : 0.3,
                        }} />
                      </div>
                    );
                  })}
                </div>
                {/* X axis labels */}
                <div style={{ marginLeft: '55px', display: 'flex', gap: chartData.length > 15 ? '2px' : '6px', marginTop: '4px' }}>
                  {chartData.map((d, i) => {
                    const showLabel = chartData.length <= 12 || i % Math.ceil(chartData.length / 8) === 0;
                    const label = chartPeriod === 'yearly'
                      ? new Date(d.label + '-01').toLocaleDateString('en-US', { month: 'short' })
                      : new Date(d.label).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    return (
                      <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.55rem', color: '#666', minWidth: 0, overflow: 'hidden' }}>
                        {showLabel ? label : ''}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Top tippers & chatters side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="stream-setup" style={{ marginBottom: 0 }}>
              <h2>Top Supporters</h2>
              {data?.top_tippers?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {data.top_tippers.map((t, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#0e0e10', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', color: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : '#cd7f32', width: '16px' }}>{i + 1}</span>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{t.tipper_name}</span>
                      </div>
                      <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '0.85rem' }}>${(t.total / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#adadb8', fontSize: '0.82rem' }}>No tips yet</p>}
            </div>

            <div className="stream-setup" style={{ marginBottom: 0 }}>
              <h2>Top Chatters</h2>
              {data?.top_chatters?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {data.top_chatters.map((c, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#0e0e10', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', color: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : '#cd7f32', width: '16px' }}>{i + 1}</span>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{c.username}</span>
                      </div>
                      <span style={{ color: '#adadb8', fontSize: '0.82rem' }}>{c.msg_count} msgs</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#adadb8', fontSize: '0.82rem' }}>No chat messages yet</p>}
            </div>
          </div>
        </div>
      )}

      {/* Stream setup */}
      {activeSection === 'stream' && <div className="stream-setup">
        <h2>🎥 Stream Setup (OBS / Streamlabs)</h2>
        <div className="code-row">
          <span className="code-label">RTMP URL</span>
          <span className="code-val">rtmps://global-live.mux.com:443/app</span>
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => copy('rtmps://global-live.mux.com:443/app', 'rtmp')}>
            {copied === 'rtmp' ? '✓' : 'Copy'}
          </button>
        </div>
        <div className="code-row">
          <span className="code-label">Stream Key</span>
          <span className="code-val">{user.mux_stream_key || '(not set up yet)'}</span>
          {user.mux_stream_key && (
            <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              onClick={() => copy(user.mux_stream_key, 'key')}>
              {copied === 'key' ? '✓' : 'Copy'}
            </button>
          )}
        </div>

        <div className="code-row">
          <span className="code-label">Chat Overlay</span>
          <span className="code-val">{`${window.location.origin}/${user.username}/chat-overlay`}</span>
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => copy(`${window.location.origin}/${user.username}/chat-overlay`, 'overlay')}>
            {copied === 'overlay' ? '✓' : 'Copy'}
          </button>
        </div>
        <div style={{ fontSize: '0.72rem', color: '#adadb8', marginBottom: '10px' }}>
          Add as a Browser Source in OBS (400x600, transparent).
        </div>

        <div className="code-row">
          <span className="code-label">Stream Mgr</span>
          <span className="code-val">{`${window.location.origin}/obs/stream-manager?token=${localStorage.getItem('token') || 'TOKEN'}`}</span>
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => copy(`${window.location.origin}/obs/stream-manager?token=${localStorage.getItem('token') || ''}`, 'obs-mgr')}>
            {copied === 'obs-mgr' ? '✓' : 'Copy'}
          </button>
        </div>
        <div className="code-row">
          <span className="code-label">Chat Dock</span>
          <span className="code-val">{`${window.location.origin}/obs/chat?token=${localStorage.getItem('token') || 'TOKEN'}&channel=${user.username}`}</span>
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => copy(`${window.location.origin}/obs/chat?token=${localStorage.getItem('token') || ''}&channel=${user.username}`, 'obs-chat')}>
            {copied === 'obs-chat' ? '✓' : 'Copy'}
          </button>
        </div>
        <div style={{ fontSize: '0.72rem', color: '#adadb8', marginBottom: '10px' }}>
          OBS → View → Docks → Custom Browser Docks. Paste the URLs above.
        </div>

        <div className="divider" />

        {/* Tag picker */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '8px' }}>
            TAGS <span style={{ fontWeight: 400 }}>({tags.length}/5)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {PRESET_TAGS.map(t => {
              const active = tags.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => setTags(prev => active ? prev.filter(x => x !== t) : prev.length < 5 ? [...prev, t] : prev)}
                  style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600,
                    cursor: tags.length >= 5 && !active ? 'not-allowed' : 'pointer',
                    border: 'none',
                    background: active ? '#D6E9AA' : '#2a2a2e',
                    color: active ? '#fff' : '#adadb8',
                    opacity: tags.length >= 5 && !active ? 0.4 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Learning tags */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '8px' }}>
            WHAT WILL VIEWERS LEARN? <span style={{ fontWeight: 400 }}>({learningTags.length}/5)</span>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {learningTags.map((t, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: '#D6E9AA22', border: '1px solid #D6E9AA33', borderRadius: '2px', fontSize: '0.78rem', color: 'var(--accent)' }}>
                {t}
                <button onClick={() => setLearningTags(prev => prev.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}>✕</button>
              </span>
            ))}
          </div>
          {learningTags.length < 5 && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <input className="input" placeholder="e.g. Python basics, watercolor technique..."
                value={learningInput} onChange={e => setLearningInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && learningInput.trim()) { e.preventDefault(); setLearningTags(prev => [...prev, learningInput.trim()]); setLearningInput(''); } }}
                style={{ marginBottom: 0, flex: 1 }} />
              <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.78rem' }}
                onClick={() => { if (learningInput.trim()) { setLearningTags(prev => [...prev, learningInput.trim()]); setLearningInput(''); } }}>Add</button>
            </div>
          )}
        </div>

        {/* Stream goal */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '8px' }}>STREAM GOAL <span style={{ fontWeight: 400 }}>(optional)</span></div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input className="input" placeholder="e.g. Finish this painting, solve 5 problems..."
              value={goalText} onChange={e => setGoalText(e.target.value)} style={{ marginBottom: 0, flex: 1 }} />
            <input className="input" type="number" min="1" placeholder="Target" value={goalTarget}
              onChange={e => setGoalTarget(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ marginBottom: 0, width: '80px' }} />
          </div>
        </div>

        <div className="go-live-form">
          <input
            className="input"
            placeholder="Stream title..."
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
          <select className="input" style={{ flex: '0 0 140px', marginBottom: 0 }} value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          {!isLive ? (
            <button className="btn btn-primary" onClick={handleGoLive}>Go Live</button>
          ) : (
            <>
              <button className="btn btn-primary" onClick={handleOpenRaid}>🎉 Raid</button>
              <button className="btn btn-danger" onClick={handleEndStream}>End Stream</button>
            </>
          )}
        </div>
        {liveMsg && <p style={{ marginTop: '10px', fontSize: '0.85rem', color: isLive ? '#00c400' : '#adadb8' }}>{liveMsg}</p>}

        {/* Raid picker */}
        {showRaid && (
          <div style={{ marginTop: '16px', background: '#0e0e10', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontWeight: 700, marginBottom: '12px' }}>
              🎉 Raid a live channel
              <button onClick={() => setShowRaid(false)} style={{ float: 'right', background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
            </div>
            {liveChannels.length === 0 ? (
              <p style={{ color: '#adadb8', fontSize: '0.9rem' }}>No other channels are live right now.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {liveChannels.map(ch => (
                  <div key={ch.username} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#18181b', borderRadius: '8px' }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{ch.display_name || ch.username}</span>
                      <span style={{ color: '#adadb8', fontSize: '0.8rem', marginLeft: '8px' }}>{ch.title}</span>
                    </div>
                    <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                      onClick={() => handleRaid(ch.username)} disabled={raiding}>
                      Raid
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Polls */}
      {activeSection === 'polls' && <div className="stream-setup">
        <h2>📊 Polls</h2>
        {activePoll && !activePoll.ended ? (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ padding: '14px', background: '#0e0e10', borderRadius: '4px', marginBottom: '8px' }}>
              <div style={{ fontWeight: 700, marginBottom: '8px' }}>{activePoll.question}</div>
              {activePoll.options.map((opt, i) => {
                const total = activePoll.votes.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? Math.round((activePoll.votes[i] / total) * 100) : 0;
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.85rem' }}>
                    <span>{opt}</span>
                    <span style={{ color: '#adadb8' }}>{activePoll.votes[i]} votes ({pct}%)</span>
                  </div>
                );
              })}
            </div>
            <button className="btn btn-danger" onClick={async () => {
              await api.endPoll(activePoll.id);
              setActivePoll(prev => ({ ...prev, ended: true }));
              setPollMsg('Poll ended');
              setTimeout(() => setPollMsg(''), 3000);
            }}>End Poll</button>
          </div>
        ) : (
          <div>
            <div className="form-group">
              <label>Question</label>
              <input className="input" placeholder="What should I play next?" value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} style={{ marginBottom: 0 }} />
            </div>
            <div className="form-group" style={{ marginTop: '10px' }}>
              <label>Options (2-6)</label>
              {pollOptions.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <input className="input" placeholder={`Option ${i + 1}`} value={opt} onChange={e => {
                    const copy = [...pollOptions]; copy[i] = e.target.value; setPollOptions(copy);
                  }} style={{ marginBottom: 0, flex: 1 }} />
                  {pollOptions.length > 2 && (
                    <button className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}>✕</button>
                  )}
                </div>
              ))}
              {pollOptions.length < 6 && (
                <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '4px 12px' }} onClick={() => setPollOptions([...pollOptions, ''])}>+ Add Option</button>
              )}
            </div>
            <div className="form-group" style={{ marginTop: '10px' }}>
              <label>Duration: {pollDuration}s</label>
              <input type="range" min="15" max="300" value={pollDuration} onChange={e => setPollDuration(parseInt(e.target.value))} style={{ width: '100%', accentColor: 'var(--accent)' }} />
            </div>
            <button className="btn btn-primary" onClick={async () => {
              const opts = pollOptions.filter(o => o.trim());
              if (!pollQuestion.trim() || opts.length < 2) { setPollMsg('Need a question and at least 2 options'); return; }
              try {
                const res = await api.createPoll({ question: pollQuestion, options: opts, duration_seconds: pollDuration });
                setActivePoll(res);
                setPollQuestion(''); setPollOptions(['', '']);
                setPollMsg('Poll is live!');
              } catch (e) { setPollMsg(e.message); }
              setTimeout(() => setPollMsg(''), 3000);
            }}>Start Poll</button>
          </div>
        )}
        {pollMsg && <p style={{ fontSize: '0.82rem', marginTop: '8px', color: pollMsg.includes('live') || pollMsg.includes('ended') ? '#00c400' : '#eb0400' }}>{pollMsg}</p>}
      </div>}

      {/* Channel Panels */}
      {activeSection === 'panels' && <div className="stream-setup">
        <h2>📋 Channel Panels</h2>
        <p style={{ fontSize: '0.82rem', color: '#adadb8', marginBottom: '20px' }}>These show on your profile page. Edit the content below.</p>

        {['About Me', 'My Set Up', 'Stream Schedule'].map(defaultTitle => {
          const existing = panels.find(p => p.title === defaultTitle);
          return (
            <div key={defaultTitle} style={{ marginBottom: '20px', padding: '16px', background: '#0e0e10', borderRadius: '4px', border: '1px solid #2a2a2e' }}>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: '10px' }}>{defaultTitle}</div>
              <textarea
                className="input"
                placeholder={
                  defaultTitle === 'About Me' ? 'Tell your viewers about yourself...' :
                  defaultTitle === 'My Set Up' ? 'What gear do you use? Camera, mic, PC specs...' :
                  'When do you stream? e.g. Mon/Wed/Fri 7pm EST'
                }
                value={existing?.content || ''}
                onChange={e => {
                  const val = e.target.value;
                  if (existing) {
                    setPanels(prev => prev.map(p => p.id === existing.id ? { ...p, content: val } : p));
                  } else {
                    setPanels(prev => [...prev, { id: `temp_${defaultTitle}`, title: defaultTitle, content: val }]);
                  }
                }}
                rows={4}
                style={{ resize: 'vertical', minHeight: '80px', fontFamily: 'inherit', marginBottom: '8px' }}
              />
              <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '6px 16px' }} onClick={async () => {
                try {
                  if (existing && !String(existing.id).startsWith('temp_')) {
                    await api.updatePanel(existing.id, { content: existing.content });
                  } else {
                    const res = await api.createPanel({ title: defaultTitle, content: existing?.content || '' });
                    setPanels(prev => prev.map(p => p.title === defaultTitle ? res : p));
                  }
                  setPanelMsg(`${defaultTitle} saved!`);
                } catch (e) { setPanelMsg(e.message); }
                setTimeout(() => setPanelMsg(''), 3000);
              }}>Save</button>
            </div>
          );
        })}
        {panelMsg && <p style={{ fontSize: '0.82rem', marginTop: '8px', color: panelMsg.includes('saved') ? '#00c400' : '#eb0400' }}>{panelMsg}</p>}
      </div>}

      {/* Chat Settings */}
      {activeSection === 'chat' && <div className="stream-setup">
        <h2>💬 Chat Settings</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Slow mode */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Slow Mode
                {chatSettings.slow_mode_seconds > 0 && (
                  <span style={{ color: '#D6E9AA', fontWeight: 400, marginLeft: '8px' }}>{chatSettings.slow_mode_seconds}s between messages</span>
                )}
              </label>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[0, 3, 5, 10, 30, 60, 120].map(s => (
                <button key={s}
                  className={`tip-amount-btn ${chatSettings.slow_mode_seconds === s ? 'selected' : ''}`}
                  onClick={() => {
                    const updated = { ...chatSettings, slow_mode_seconds: s };
                    setChatSettings(updated);
                    api.updateChatSettings(updated).then(() => setChatSettingsMsg('Saved!')).catch(e => setChatSettingsMsg(e.message));
                    setTimeout(() => setChatSettingsMsg(''), 2000);
                  }}
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                  {s === 0 ? 'Off' : `${s}s`}
                </button>
              ))}
            </div>
          </div>

          {/* Follower only */}
          <div className="toggle-row">
            <button className={`toggle ${chatSettings.follower_only ? 'on' : ''}`}
              onClick={() => {
                const updated = { ...chatSettings, follower_only: !chatSettings.follower_only };
                setChatSettings(updated);
                api.updateChatSettings(updated).catch(() => {});
              }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Follower-Only Chat</div>
              <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>Only followers can send messages</div>
            </div>
          </div>

          {/* Sub only */}
          <div className="toggle-row">
            <button className={`toggle ${chatSettings.sub_only ? 'on' : ''}`}
              onClick={() => {
                const updated = { ...chatSettings, sub_only: !chatSettings.sub_only };
                setChatSettings(updated);
                api.updateChatSettings(updated).catch(() => {});
              }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Subscriber-Only Chat</div>
              <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>Only subscribers can send messages</div>
            </div>
          </div>

          {/* Links */}
          <div className="toggle-row">
            <button className={`toggle ${!chatSettings.links_allowed ? 'on' : ''}`}
              onClick={() => {
                const updated = { ...chatSettings, links_allowed: !chatSettings.links_allowed };
                setChatSettings(updated);
                api.updateChatSettings(updated).catch(() => {});
              }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Block Links</div>
              <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>Prevent non-mods from posting URLs</div>
            </div>
          </div>

          {/* Account age */}
          <div className="toggle-row">
            <button className={`toggle ${chatSettings.account_age_required ? 'on' : ''}`}
              onClick={() => {
                const updated = { ...chatSettings, account_age_required: !chatSettings.account_age_required };
                setChatSettings(updated);
                api.updateChatSettings(updated).catch(() => {});
              }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Account Age Required</div>
              <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>Accounts must be 10+ minutes old to chat</div>
            </div>
          </div>

          {chatSettingsMsg && <p style={{ fontSize: '0.82rem', color: '#00c400' }}>{chatSettingsMsg}</p>}
          <p style={{ fontSize: '0.75rem', color: '#adadb8' }}>
            Auto-mod is always on: blocks excessive caps, repeated characters, and rapid duplicate messages.
          </p>
        </div>
      </div>}

      {/* Moderation */}
      {activeSection === 'moderation' && <div className="stream-setup">
        <h2>🔨 Moderation</h2>

        {/* Mods */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '10px' }}>MODERATORS</div>
          {mods.length === 0 && <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '10px' }}>No mods yet. Type <code style={{ background: '#0e0e10', padding: '1px 5px', borderRadius: 3 }}>/mod username</code> in chat or add below.</p>}
          {mods.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
              {mods.map(m => (
                <div key={m.username} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0e0e10', borderRadius: '8px' }}>
                  <span style={{ flex: 1, fontWeight: 600 }}>{m.display_name || m.username} <span style={{ color: '#adadb8', fontWeight: 400 }}>@{m.username}</span></span>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={async () => {
                    await api.removeMod(user.username, m.username);
                    setMods(prev => prev.filter(x => x.username !== m.username));
                  }}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input className="input" placeholder="Username to mod" value={modInput} onChange={e => setModInput(e.target.value)} style={{ marginBottom: 0, flex: 1 }} />
            <button className="btn btn-primary" onClick={async () => {
              try {
                await api.addMod(user.username, { username: modInput.trim().toLowerCase() });
                const updated = await api.listMods(user.username);
                setMods(updated);
                setModInput('');
                setModMsg('✅ Mod added!');
              } catch (e) { setModMsg(`❌ ${e.message}`); }
              setTimeout(() => setModMsg(''), 3000);
            }}>Add Mod</button>
          </div>
          {modMsg && <p style={{ fontSize: '0.82rem', marginTop: '8px' }}>{modMsg}</p>}
        </div>

        {/* Bans */}
        <div>
          <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '10px' }}>BANNED USERS</div>
          {bans.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: '#adadb8' }}>No banned users. Hover over a chat message and click 🚫, or type <code style={{ background: '#0e0e10', padding: '1px 5px', borderRadius: 3 }}>/ban username</code> in chat.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {bans.map(b => (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0e0e10', borderRadius: '8px' }}>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{b.display_name || b.username}</span>
                    {b.reason && <span style={{ color: '#adadb8', fontSize: '0.8rem', marginLeft: '8px' }}>{b.reason}</span>}
                  </span>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={async () => {
                    await api.unbanUser(user.username, b.username);
                    setBans(prev => prev.filter(x => x.id !== b.id));
                  }}>Unban</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}

      {/* Channel Points */}
      {activeSection === 'points' && <div className="stream-setup">
        <h2>💜 Channel Points</h2>

        {/* Create reward */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '10px' }}>CREATE REWARD</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <input className="input" placeholder="Reward title" value={rewardTitle} onChange={e => setRewardTitle(e.target.value)}
              style={{ flex: '1 1 200px', marginBottom: 0 }} />
            <input className="input" type="number" placeholder="Cost (pts)" value={rewardCost} onChange={e => setRewardCost(parseInt(e.target.value) || 0)}
              style={{ flex: '0 0 120px', marginBottom: 0 }} />
            <input className="input" placeholder="Description (optional)" value={rewardDesc} onChange={e => setRewardDesc(e.target.value)}
              style={{ flex: '1 1 180px', marginBottom: 0 }} />
            <button className="btn btn-primary" onClick={async () => {
              try {
                const r = await api.createReward({ title: rewardTitle, cost: rewardCost, description: rewardDesc });
                setRewards(prev => [...prev, r]);
                setRewardTitle(''); setRewardCost(500); setRewardDesc('');
                setRewardMsg('✅ Reward created!');
              } catch (e) { setRewardMsg(`❌ ${e.message}`); }
              setTimeout(() => setRewardMsg(''), 3000);
            }}>Add</button>
          </div>
          {rewardMsg && <p style={{ fontSize: '0.82rem', marginTop: '8px' }}>{rewardMsg}</p>}
        </div>

        {/* Rewards list */}
        {rewards.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '10px' }}>YOUR REWARDS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {rewards.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#0e0e10', borderRadius: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{r.title}</span>
                    {r.description && <span style={{ color: '#adadb8', fontSize: '0.8rem', marginLeft: '8px' }}>{r.description}</span>}
                  </div>
                  <span style={{ color: '#D6E9AA', fontWeight: 700, fontSize: '0.85rem' }}>{r.cost.toLocaleString()} pts</span>
                  <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={async () => {
                    await api.deleteReward(r.id);
                    setRewards(prev => prev.filter(x => x.id !== r.id));
                  }}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending redemptions */}
        {redemptions.filter(r => r.status === 'pending').length > 0 && (
          <div>
            <div style={{ fontSize: '0.8rem', color: '#adadb8', fontWeight: 600, marginBottom: '10px' }}>PENDING REDEMPTIONS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {redemptions.filter(r => r.status === 'pending').map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#0e0e10', borderRadius: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{r.display_name || r.username}</span>
                    <span style={{ color: '#adadb8', fontSize: '0.85rem', marginLeft: '8px' }}>wants "{r.reward_title}"</span>
                  </div>
                  <span style={{ color: '#D6E9AA', fontWeight: 700, fontSize: '0.82rem' }}>{r.cost.toLocaleString()} pts</span>
                  <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={async () => {
                    await api.fulfillRedemption(r.id);
                    setRedemptions(prev => prev.map(x => x.id === r.id ? { ...x, status: 'fulfilled' } : x));
                  }}>✓ Done</button>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={async () => {
                    await api.rejectRedemption(r.id);
                    setRedemptions(prev => prev.map(x => x.id === r.id ? { ...x, status: 'rejected' } : x));
                  }}>✕ Reject</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {rewards.length === 0 && redemptions.length === 0 && (
          <p style={{ color: '#adadb8', fontSize: '0.85rem' }}>Create rewards for your viewers to redeem with channel points. Viewers earn 10 pts/min while watching live.</p>
        )}
      </div>}

      {/* Recent tips */}
      {activeSection === 'earnings' && data?.recent_tips?.length > 0 && (
        <div className="stream-setup">
          <h2>💜 Recent Tips</h2>
          <table className="tips-table">
            <thead>
              <tr>
                <th>From</th>
                <th>Amount</th>
                <th>Message</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_tips.map((t, i) => (
                <tr key={i}>
                  <td>{t.tipper_name}</td>
                  <td style={{ color: '#D6E9AA', fontWeight: 600 }}>${(t.amount_cents / 100).toFixed(2)}</td>
                  <td style={{ color: '#adadb8' }}>{t.message || '—'}</td>
                  <td style={{ color: '#adadb8', fontSize: '0.8rem' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
